import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { BuildingType } from '../../data';
 
interface ShadowPathConfig {
  points: number[];
  generator: (d: number, points: number[]) => string;
}
 
@Component({
    selector: '[app-shadow]',
    templateUrl: './shadow.component.html',
    styleUrl: './shadow.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ShadowComponent {
  type = input<BuildingType>();
  height = input<number>();
 
  private readonly SHADOW_CONFIGS = new Map<BuildingType, ShadowPathConfig>([
    [BuildingType.Marketplace, {
      points: [238, 533, 108, 530, 135, 428, 259, 304, 454, 416],
      generator: this.generateStandardShadowPath.bind(this)
    }],
    [BuildingType.Warehouse, {
      points: [240, 434, 110, 432, 136, 319, 200, 265, 356, 368],
      generator: this.generateStandardShadowPath.bind(this)
    }],
    [BuildingType.Library, {
      points: [291, 461, 150, 457, 179, 346, 238, 260, 360, 307, 402, 303, 493, 356],
      generator: this.generateLibraryShadowPath.bind(this)
    }],
    [BuildingType.Management, {
      points: [182, 331, 91, 326, 175, 161, 268, 208, 324, 240],
      generator: this.generateStandardShadowPath.bind(this)
    }],
    [BuildingType.Monitoring_Reporting, {
      points: [178, 387, 204, 290, 224, 224, 360, 309, 218, 383, 178, 387],
      generator: this.generateMonitorShadowPath.bind(this)
    }]
  ]);
 
  shadowPath = computed(() => {
    const currentType = this.type();
    const config = this.SHADOW_CONFIGS.get(currentType);
    if (!config) return '';
    const delta = this.calculateDelta(this.height(), currentType);
    return config.generator(delta, config.points);
  });
 
  private calculateDelta(height: number, type: BuildingType): number {
    const multiplier = type === BuildingType.Management ? 0.4 : 0.27;
    return 100 - multiplier * height;
  }
 
  private generateStandardShadowPath(d: number, points: number[]): string {
    return this.createPath([
      [points[0] - d, points[1] - d],
      [points[2], points[3]],
      [points[4] + d / 2, d >= 10 ? points[5] : points[5] - 10],
      [points[6], points[7] + d],
      [points[8] - 2 * d, points[9]],
    ]);
  }
 
  private generateLibraryShadowPath(d: number, points: number[]): string {
    return this.createPath([
      [points[0] - d, points[1] - d],
      [points[2] + d, points[3] - d],
      [points[4] + d, points[5] + d],
      [points[6] + d, points[7] + d],
      [points[8] - d, points[9] + d],
      [points[10] - d, points[11] + d],
      [points[12] - 2.5 * d, points[13]],
    ]);
  }
 
  private generateMonitorShadowPath(d: number, points: number[]): string {
    return this.createPath([
      [points[0], points[1]],
      [points[2], points[3]],
      [points[4], points[5] + d],
      [points[6] - d, points[7]],
      [points[8] - d, points[9]],
      [points[10] + d, points[11]],
    ]);
  }
 
  private createPath(points: [number, number][]): string {
    return 'M' + points.map(([x, y]) => `${Math.round(x)} ${Math.round(y)}`).join(' L') + 'Z';
  }
}